import { Product } from '@inter-ikea-storageone/range';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import SizeFilter from 'ui/SizeFilter/SizeFilter';
import { ProductType } from '@inter-ikea-storageone/range';
import * as R from 'ramda';
import { useCore } from 'contexts/core';
import { findMaxFrameSizeInScene, getFilterOptions, getFilterParams, getUnit } from './utils';
import { FilterType } from 'rangeConfig/@types';
import { useSelector } from 'react-redux';
import { getMarketProducts } from 'ducks/products';

type DepthProps = {
  products: Product[];
  filterState: any;
  type: FilterType;
  onFilter: (productIdList: string[], filterState: any) => void;
};

const DepthFilter = ({ products, filterState, onFilter, type }: DepthProps) => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const allProducts = useSelector(getMarketProducts);

  const availableDepths = getFilterOptions('depth')(
    products.filter(p => p.type !== ProductType.PAX_CORNER_SOLUTION_ASM)
  );

  const defaultDepth =
    type === FilterType.TOGGLE ? (R.last(Object.keys(availableDepths).sort()) as string) : null;
  const unit = getUnit(products);

  const onDepthFilter = useCallback(
    (size: number) => {
      const { productIds, newFilterState } = getFilterParams(
        type,
        size,
        'depth',
        products,
        allProducts,
        filterState
      );
      onFilter(productIds, newFilterState);
    },
    [allProducts, filterState, onFilter, products, type]
  );

  const onLoad = () => {
    if (type === FilterType.FILTER || defaultDepth === null) return;
    const maxFrameDepthInScene = findMaxFrameSizeInScene(coreApi, 'depth');
    if (maxFrameDepthInScene !== null && maxFrameDepthInScene !== filterState.selected) {
      onDepthFilter(maxFrameDepthInScene);
    }
  };

  useEffect(() => {
    if (defaultDepth && (filterState.selected === undefined || filterState.selected === null)) {
      // set initial filter value
      onDepthFilter(parseInt(defaultDepth, 10));
    }
  }, [defaultDepth, filterState.selected, onDepthFilter]);

  useEffect(() => {
    return coreApi.subscribe('load', onLoad);
  });

  if (!products.length) {
    return null;
  }
  const label = `${formatMessage({ id: 'storageone.depth' })} (${unit}):`;

  return (
    <SizeFilter
      id="depth-id"
      data-testid="depth-id"
      label={label}
      name="depth-filter"
      sizes={availableDepths}
      onFilter={onDepthFilter}
      filterState={filterState}
    />
  );
};

export default DepthFilter;
